import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual"

interface VirtualStorageListProps<T> {
  items: T[]
  itemKey: (item: T) => string
  isItemFocusable?: (item: T) => boolean
  children: (item: T, index: number) => ReactNode
  height?: number
  fill?: boolean
  pinnedKeys?: string[]
  scrollToKey?: string
  footer?: ReactNode
  onEndReached?: () => void
  onVisible?: (items: T[]) => void
  onKeyDown?: (event: React.KeyboardEvent, item: T, index: number) => void
}

// Queries and interaction state belong to callers; only visible controls mount here.
export function VirtualStorageList<T>({
  items,
  itemKey,
  isItemFocusable,
  children,
  height = 320,
  fill = false,
  pinnedKeys = [],
  scrollToKey,
  onVisible,
  footer,
  onEndReached,
  onKeyDown,
}: VirtualStorageListProps<T>) {
  const viewport = useRef<HTMLDivElement>(null)
  const [focusedKey, setFocusedKey] = useState<string>()
  const getItemKey = useCallback(
    (index: number) => (index === items.length ? "storage-continuation" : itemKey(items[index])),
    [items, itemKey]
  )
  const pinned = items.flatMap((item, index) =>
    itemKey(item) === focusedKey || pinnedKeys.includes(itemKey(item)) ? [index] : []
  )
  const virtualizer = useVirtualizer({
    count: items.length + (footer ? 1 : 0),
    getScrollElement: () => viewport.current,
    estimateSize: () => 40,
    getItemKey,
    overscan: 5,
    rangeExtractor: (range) => [...new Set([...defaultRangeExtractor(range), ...pinned])].sort((a, b) => a - b),
  })
  const visible = virtualizer.getVirtualItems()
  const visibleSignature = visible.map((row) => row.index).join(",")

  const scrollTargetIndex = scrollToKey === undefined ? -1 : items.findIndex((item) => itemKey(item) === scrollToKey)
  useEffect(() => {
    if (scrollTargetIndex >= 0) virtualizer.scrollToIndex(scrollTargetIndex, { align: "auto" })
  }, [scrollToKey, scrollTargetIndex, virtualizer])

  useEffect(() => {
    // Pinned interaction rows must not trigger pagination away from the viewport.
    const range = virtualizer.range
    if (range) {
      onVisible?.(items.slice(Math.max(0, range.startIndex - 5), range.endIndex + 6))
      if (range.endIndex + 5 >= items.length) onEndReached?.()
    }
  }, [visibleSignature, items, onVisible, onEndReached, virtualizer])

  function focusIndex(index: number, direction: number) {
    let next = Math.max(0, Math.min(items.length - 1, index))
    while (next >= 0 && next < items.length && isItemFocusable?.(items[next]) === false) next += direction
    if (next < 0 || next >= items.length) return
    virtualizer.scrollToIndex(next)
    setFocusedKey(itemKey(items[next]))
    requestAnimationFrame(() => {
      viewport.current
        ?.querySelector<HTMLElement>(
          `[data-index="${next}"] [data-row-focus], [data-index="${next}"] button:not(:disabled), [data-index="${next}"] input:not(:disabled)`
        )
        ?.focus()
    })
  }

  return (
    <div
      ref={viewport}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocusedKey(undefined)
      }}
      className="min-h-0 overflow-auto"
      style={{
        height: fill ? "100%" : Math.min(height, virtualizer.getTotalSize()),
        maxHeight: fill ? undefined : height,
      }}
    >
      <div style={{ height: virtualizer.getTotalSize(), position: "relative", width: "100%" }}>
        {visible.map((row) => (
          <div
            key={row.key}
            data-index={row.index}
            ref={virtualizer.measureElement}
            style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${row.start}px)` }}
            onFocusCapture={() => {
              if (row.index < items.length) setFocusedKey(itemKey(items[row.index]))
            }}
            onKeyDown={(event) => {
              if (row.index >= items.length) return
              onKeyDown?.(event, items[row.index], row.index)
              if (event.defaultPrevented || (event.target instanceof HTMLInputElement && event.target.type !== "radio"))
                return
              const target =
                event.key === "ArrowDown"
                  ? row.index + 1
                  : event.key === "ArrowUp"
                    ? row.index - 1
                    : event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? items.length - 1
                        : undefined
              if (target !== undefined) {
                event.preventDefault()
                focusIndex(target, event.key === "ArrowUp" || event.key === "End" ? -1 : 1)
              }
            }}
          >
            {row.index === items.length ? footer : children(items[row.index], row.index)}
          </div>
        ))}
      </div>
    </div>
  )
}
